// ===============================
// QrEventTable.jsx  (행사 관리 - 카드 그리드)
// ✅ 요구사항 반영:
// - 진행중(오늘) → 예정 → 종료 정렬
// - 예정=초록, 오늘=파랑, 종료=빨강 테두리
// - D-3 / D+2 / TODAY 배지
// - date 포맷: YYYY-MM-DD / YYYY-MM-DD HH:mm:ss / ISO / YYYY/MM/DD 지원
// - 종료 행사는 기본 접기 + “종료 보기”로 펼치기
// - ✅ 카드에서 "참가자 관리"로 바로 이동 버튼 추가 (onGoParticipants)
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { qrEventsApi } from "../../api/qrEventsApi";
import ActionButton from "../../../../component/common/ActionButton";

// MUI
import {
    Box,
    Button,
    Stack,
    TextField,
    Tooltip,
    Typography,
    IconButton,
    Card,
    CardContent,
    CardActions,
    Chip,
    Divider,
    Collapse, Paper,
} from "@mui/material";
import { notify } from "../../../../util/toast";
import { copyToClipboard } from "../../../../util/clipboard";
import FormField from "../../../../component/common/FormField";
import ComboBox from "../../../../component/common/ComboBox";
import { ds } from "../../../../styles/designSystem";
import FormModal from "../../../../component/common/FormModal";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const emptyEvent = { eventName: "", date: "", place: "", client: "" };

/**
 * dateStr 지원 포맷:
 * - "YYYY-MM-DD"
 * - "YYYY-MM-DD HH:mm:ss"
 * - "YYYY-MM-DDTHH:mm:ss"
 * - "YYYY/MM/DD"
 *
 * 목적: "날짜" 단위 비교를 안정적으로 수행 (로컬 타임존 기준)
 */
const parseToLocalDateOnly = (dateStr) => {
    if (!dateStr) return null;

    const s = String(dateStr).trim();
    const datePart = s.split("T")[0].split(" ")[0];
    const norm = datePart.replaceAll("/", "-");

    const [y, m, d] = norm.split("-").map((v) => Number(v));
    if (!y || !m || !d) return null;

    return new Date(y, m - 1, d);
};

const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const getStatus = (dateStr) => {
    const d = parseToLocalDateOnly(dateStr);
    if (!d) return "예정";
    const today = startOfToday();

    if (d.getTime() === today.getTime()) return "진행중(오늘)";
    if (d.getTime() < today.getTime()) return "종료";
    return "예정";
};

const getDdayBadge = (dateStr) => {
    const d = parseToLocalDateOnly(dateStr);
    if (!d) return null;

    const today = startOfToday();
    const diffDays = Math.round((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

    if (diffDays === 0) return "TODAY";
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
};

// ✅ 색상 규칙: 예정=초록, 오늘=파랑, 종료=빨강
const statusStyle = (status) => {
    if (status === "예정") {
        return {
            borderColor: "#86efac",
            chipSx: { bgcolor: ds.colors.successBg, color: ds.colors.success, borderColor: "#86efac" },
            label: "예정",
        };
    }
    if (status === "진행중(오늘)") {
        return {
            borderColor: ds.colors.primaryBorder,
            chipSx: { bgcolor: ds.colors.primaryBg, color: ds.colors.primary, borderColor: ds.colors.primaryBorder },
            label: "오늘",
        };
    }
    return {
        borderColor: "#fecaca",
        chipSx: { bgcolor: ds.colors.errorBg, color: ds.colors.error, borderColor: "#fecaca" },
        label: "종료",
    };
};

// ✅ 정렬 우선순위: 오늘(0) → 예정(1) → 종료(2)
const statusRank = (status) => {
    if (status === "진행중(오늘)") return 0;
    if (status === "예정") return 1;
    return 2;
};

export default function QrEventTable({ onGoParticipants }) {
    const [events, setEvents] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [mode, setMode] = useState("add"); // add | edit | delete
    const [currentEvent, setCurrentEvent] = useState(emptyEvent);

    const [openLinkDialog, setOpenLinkDialog] = useState(false);

    // 상태 필터
    const [statusFilter, setStatusFilter] = useState("전체");

    // 종료 목록 접기/펼치기
    const [showEnded, setShowEnded] = useState(false);

    // 검색 — 키워드(행사명·위치·발주처 부분일치) + 기간(from~to)
    const [searchKeyword, setSearchKeyword] = useState("");
    const [searchFrom, setSearchFrom] = useState("");
    const [searchTo, setSearchTo] = useState("");

    const clearSearch = () => {
        setSearchKeyword("");
        setSearchFrom("");
        setSearchTo("");
    };


    const fetchEvents = async () => {
        try {
            const response = await qrEventsApi.getEvents();
            setEvents(Array.isArray(response) ? response : (response?.data || response || []));
        } catch (error) {
            if (error.response?.data?.message === "No permission") {
                notify("행사 목록 확인 권한이 없습니다", "error");
            } else {
                notify("행사 목록을 가져오는 중 오류가 발생했습니다.", "error");
            }
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line
    }, []);

    const openModal = (type, event = emptyEvent) => {
        setMode(type);
        setCurrentEvent(event);
        setOpenDialog(true);
    };

    const closeModal = () => setOpenDialog(false);

    const openLinks = (event) => {
        setCurrentEvent(event);
        setOpenLinkDialog(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentEvent((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const { eventName, date, place, client } = currentEvent || {};
        if (!eventName || !date || !place || !client) {
            notify("모든 필드를 채워주세요.", "warning");
            return;
        }

        try {
            closeModal();

            if (mode === "add") {
                await qrEventsApi.createEvent(currentEvent);
                notify("추가가 완료되었습니다.", "success");
            } else if (mode === "edit") {
                await qrEventsApi.updateEvent(currentEvent.eventName, currentEvent);
                notify("수정이 완료되었습니다.", "success");
            }

            fetchEvents();
        } catch (error) {
            console.error("저장 오류", error);
            if (error.message?.includes("No permission") || error.message?.includes("권한")) {
                notify("행사 생성 권한이 없습니다", "error");
            } else {
                notify(error.message || "중복되는 행사명이거나 서버 오류가 발생했습니다.", "error");
            }
        }
    };

    const handleDelete = async () => {
        try {
            closeModal();
            await qrEventsApi.deleteEvent(currentEvent.eventName);
            notify("삭제가 완료되었습니다.", "success");
            fetchEvents();
        } catch (error) {
            console.error("삭제 오류", error);
            notify("행사 삭제 중 오류가 발생했습니다.", "error");
        }
    };

    const linkRequest = useMemo(() => {
        if (!currentEvent?.eventName) return "";
        return `https://onandme.info/qr-event-participant-request/${encodeURIComponent(currentEvent.eventName)}`;
    }, [currentEvent?.eventName]);

    const linkCheck = useMemo(() => {
        if (!currentEvent?.eventName) return "";
        return `https://onandme.info/qr-event-participant-check/${encodeURIComponent(currentEvent.eventName)}`;
    }, [currentEvent?.eventName]);

    const copy = async (text) => {
        const ok = await copyToClipboard(text);
        notify(ok ? "복사되었습니다." : "복사 실패 (브라우저 권한 확인)", ok ? "success" : "warning");
    };

    // 상태/정렬/필터/검색/접기 로직
    const computed = useMemo(() => {
        const arr = Array.isArray(events) ? [...events] : [];

        const enriched = arr.map((e) => {
            const status = getStatus(e?.date);
            const dateObj = parseToLocalDateOnly(e?.date);
            return {
                ...e,
                __status: status,
                __dateObj: dateObj,
                __badge: getDdayBadge(e?.date),
                __rank: statusRank(status),
            };
        });

        // 기본 정렬: 오늘 → 예정 → 종료, 같은 상태 내 날짜 오름차순
        enriched.sort((a, b) => {
            if (a.__rank !== b.__rank) return a.__rank - b.__rank;

            const at = a.__dateObj?.getTime?.() ?? Number.MAX_SAFE_INTEGER;
            const bt = b.__dateObj?.getTime?.() ?? Number.MAX_SAFE_INTEGER;
            return at - bt;
        });

        // 상태 필터 (전체/오늘/예정/종료)
        const filtered =
            statusFilter === "전체" ? enriched : enriched.filter((e) => e.__status === statusFilter);

        // 검색 매칭 — 키워드(행사명·위치·발주처) AND 기간(from~to) AND 결과
        const kw = String(searchKeyword || "").trim().toLowerCase();
        const fromObj = parseToLocalDateOnly(searchFrom);
        const toObj = parseToLocalDateOnly(searchTo);
        const searchActive = !!(kw || fromObj || toObj);

        const isMatched = (e) => {
            if (kw) {
                const hay = `${e.eventName || ""} ${e.place || ""} ${e.client || ""}`.toLowerCase();
                if (!hay.includes(kw)) return false;
            }
            if (fromObj && (!e.__dateObj || e.__dateObj.getTime() < fromObj.getTime())) return false;
            if (toObj && (!e.__dateObj || e.__dateObj.getTime() > toObj.getTime())) return false;
            return true;
        };

        const matched = searchActive ? filtered.filter(isMatched) : [];
        const matchedKeys = new Set(matched.map((e) => e.eventName));

        // 검색 매칭은 상단 "검색결과" 섹션으로 옮기고, 나머지는 그대로 기존 그리드 유지
        const restActive = filtered.filter((e) => e.__status !== "종료" && !matchedKeys.has(e.eventName));
        const restEnded = filtered.filter((e) => e.__status === "종료" && !matchedKeys.has(e.eventName));

        return {
            matched,
            active: restActive,
            ended: restEnded,
            searchActive,
            total: filtered.length,
        };
    }, [events, statusFilter, searchKeyword, searchFrom, searchTo]);

    return (
        <Box>
            {/* 헤더 */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography sx={ds.typography.sectionTitle}>행사 관리</Typography>
                        <Tooltip title="카드 그리드로 표시되고, TODAY/D-day 배지가 자동 표시됩니다. 종료 행사는 기본 접기입니다.">
                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </Tooltip>
                    </Stack>
                    <Typography sx={ds.typography.caption}>
                        정렬 순서 : 진행중(오늘) → 예정 → 종료
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <Box sx={{ width: 180 }}>
                        <ComboBox
                            clearable={false}
                            placeholder="상태"
                            options={[
                                { value: "전체", label: "전체" },
                                { value: "진행중(오늘)", label: "진행중(오늘)" },
                                { value: "예정", label: "예정" },
                                { value: "종료", label: "종료" },
                            ]}
                            value={statusFilter}
                            onChange={(v) => setStatusFilter(v)}
                        />
                    </Box>

                    <ActionButton variant="add" startIcon={<AddIcon />}
                        onClick={() => openModal("add", emptyEvent)}
                        label="행사 추가" />
                </Stack>
            </Stack>

            {/* 검색 패널 — 키워드(행사명·위치·발주처) + 기간(from~to). 매칭은 상단 "검색결과" 섹션으로. */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: ds.colors.border }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 84 }}>
                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>행사 검색</Typography>
                    </Stack>
                    <TextField
                        size="small"
                        placeholder="행사명·위치·발주처"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        sx={{ flex: 1, minWidth: 200 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="시작일"
                        InputLabelProps={{ shrink: true }}
                        value={searchFrom}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSearchFrom(v);
                            // 시작이 종료보다 뒤가 되면 종료도 자동 보정 (broken filter 방지)
                            if (v && searchTo && v > searchTo) setSearchTo(v);
                        }}
                        sx={{ width: { xs: "100%", md: 170 } }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="종료일"
                        InputLabelProps={{ shrink: true }}
                        value={searchTo}
                        onChange={(e) => {
                            const v = e.target.value;
                            setSearchTo(v);
                            if (v && searchFrom && v < searchFrom) setSearchFrom(v);
                        }}
                        sx={{ width: { xs: "100%", md: 170 } }}
                    />
                    <ActionButton variant="secondary"
                        startIcon={<ClearIcon />}
                        onClick={clearSearch}
                        disabled={!computed.searchActive}
                        sx={{ minWidth: 96 }}
                        label="초기화" />
                </Stack>
            </Paper>

            {/* 검색결과 섹션 — 검색 활성 시 매칭된 행사들을 상단에 별도 표시 */}
            {computed.searchActive && (
                <Box sx={{ mb: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <SearchIcon sx={{ fontSize: 18, color: ds.colors.primary }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: ds.colors.primary }}>
                            검색결과 ({computed.matched.length}건)
                        </Typography>
                    </Stack>
                    {computed.matched.length === 0 ? (
                        <Box
                            sx={{
                                border: `1px dashed ${ds.colors.border}`,
                                borderRadius: 1.5,
                                py: 4,
                                textAlign: "center",
                                color: "text.secondary",
                                fontSize: 13,
                            }}
                        >
                            조건에 맞는 행사가 없습니다.
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                    lg: "repeat(4, 1fr)",
                                },
                            }}
                        >
                            {computed.matched.map((event, index) => {
                                const status = event.__status;
                                const st = statusStyle(status);
                                return (
                                    <Card
                                        key={`matched-${event.eventName}-${index}`}
                                        variant="outlined"
                                        sx={{
                                            ...ds.card,
                                            borderRadius: 1.5,
                                            borderColor: ds.colors.primaryBorder,
                                            // 검색결과 강조 — 옅은 primary 배경
                                            bgcolor: ds.colors.primaryBg,
                                            overflow: "hidden",
                                            display: "flex",
                                            flexDirection: "column",
                                            minHeight: 240,
                                        }}
                                    >
                                        <CardContent sx={{ pb: 1.25 }}>
                                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{event.eventName}</Typography>
                                                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>검색결과</Typography>
                                                </Box>
                                                <Stack direction="column" spacing={0.75} alignItems="flex-end">
                                                    <Chip size="small" label={st.label} variant="outlined" sx={{ ...st.chipSx, fontWeight: 700 }} />
                                                    {event.__badge && (
                                                        <Chip size="small" label={event.__badge} variant="outlined" sx={{ ...st.chipSx, fontWeight: 700 }} />
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Divider sx={{ my: 1.25 }} />
                                            <Stack spacing={0.75}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <EventIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{event.date || "-"}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PlaceIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                    <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>{event.place || "-"}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                    <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>{event.client || "-"}</Typography>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <CardActions sx={{ px: 2, pb: 2, pt: 1 }}>
                                            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                                <ActionButton variant="primary" startIcon={<GroupIcon />}
                                                    onClick={() => onGoParticipants?.(event.eventName)}
                                                    sx={{ flex: 1 }} label="명단" />
                                                <ActionButton variant="edit"
                                                    onClick={() => openModal("edit", event)} sx={{ flex: 1 }} />
                                                <ActionButton variant="delete"
                                                    onClick={() => openModal("delete", event)} sx={{ flex: 1 }} />
                                                {/*<ActionButton variant="secondary" startIcon={<LinkIcon />} onClick={() => openLinks(event)} sx={{ flex: 1 }} label="링크" />*/}
                                            </Stack>
                                        </CardActions>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}

            {/* 진행중/예정 라벨 — 검색 모드일 때 구분 명확화 */}
            {computed.searchActive && computed.active.length > 0 && (
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary", mb: 1 }}>
                    진행중/예정 ({computed.active.length}건)
                </Typography>
            )}

            {/* 활성(진행중/예정) 카드 그리드 */}
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                    },
                }}
            >
                {computed.active.map((event, index) => {
                    const status = event.__status;
                    const st = statusStyle(status);

                    return (
                        <Card
                            key={`${event.eventName}-${index}`}
                            variant="outlined"
                            sx={{
                                ...ds.card,
                                borderRadius: 1.5,
                                borderColor: ds.colors.border,
                                overflow: "hidden",
                                transition: "transform 0.12s ease, box-shadow 0.12s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                        boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                                },
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 240,
                            }}
                        >
                            <CardContent sx={{ pb: 1.25 }}>
                                {/* 상단: 제목 + 상태칩 + D-day 배지 */}
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
                                            {event.eventName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                            행사명
                                        </Typography>
                                    </Box>

                                    <Stack direction="column" spacing={0.75} alignItems="flex-end">
                                        <Chip size="small" label={st.label} variant="outlined" sx={{ ...st.chipSx, fontWeight: 700 }} />
                                        {event.__badge && (
                                            <Chip
                                                size="small"
                                                label={event.__badge}
                                                variant="outlined"
                                                sx={{ ...st.chipSx, fontWeight: 700 }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 1.25 }} />

                                <Stack spacing={0.75}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <EventIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                           {event.date || "-"}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <PlaceIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                        <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
                                            {event.place || "-"}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                        <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
                                            {event.client || "-"}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>

                            <Box sx={{ flexGrow: 1 }} />

                            <CardActions sx={{ px: 2, pb: 2, pt: 1 }}>
                                <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                    <ActionButton variant="primary" startIcon={<GroupIcon />}
                                        onClick={() => onGoParticipants?.(event.eventName)}
                                        sx={{ flex: 1 }} label="명단" />
                                    <ActionButton variant="edit"
                                        onClick={() => openModal("edit", event)}
                                        sx={{ flex: 1 }} />
                                    <ActionButton variant="delete"
                                        onClick={() => openModal("delete", event)}
                                        sx={{ flex: 1 }} />
                                    {/*<ActionButton variant="secondary" startIcon={<LinkIcon />}*/}
                                    {/*    onClick={() => openLinks(event)}*/}
                                    {/*    sx={{ flex: 1 }} label="링크" />*/}
                                </Stack>
                            </CardActions>
                        </Card>
                    );
                })}
            </Box>

            {/* 종료 섹션: 기본 접기 */}
            <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>종료된 행사 ({computed.ended.length})</Typography>

                    <ActionButton variant="secondary"
                        startIcon={false}
                        onClick={() => setShowEnded((v) => !v)}
                        disabled={computed.ended.length === 0}
                        sx={{
                            color: ds.colors.primary, borderColor: "transparent",
                            "&:hover": { borderColor: "transparent", bgcolor: ds.colors.primaryBg, color: ds.colors.primary },
                        }}
                        label={showEnded
                            ? <Stack direction="row" alignItems="center" spacing={0.5}><span>종료 숨기기</span><ExpandLessIcon sx={{ fontSize: 16 }} /></Stack>
                            : <Stack direction="row" alignItems="center" spacing={0.5}><span>종료 보기</span><ExpandMoreIcon sx={{ fontSize: 16 }} /></Stack>}
                    />
                </Stack>

                <Collapse in={showEnded} unmountOnExit>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: "repeat(4, 1fr)",
                            },
                        }}
                    >
                        {computed.ended.map((event, index) => {
                            const st = statusStyle("종료");

                            return (
                                <Card
                                    key={`${event.eventName}-ended-${index}`}
                                    variant="outlined"
                                    sx={{
                                        ...ds.card,
                                        borderRadius: 1.5,
                                        borderColor: ds.colors.border,
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: 240,
                                    }}
                                >
                                    <CardContent sx={{ pb: 1.25 }}>
                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
                                                    {event.eventName}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                                    종료된 행사
                                                </Typography>
                                            </Box>

                                            <Stack direction="column" spacing={0.75} alignItems="flex-end">
                                                <Chip size="small" label="종료" variant="outlined" sx={{ ...st.chipSx, fontWeight: 700 }} />
                                                {getDdayBadge(event?.date) && (
                                                    <Chip
                                                        size="small"
                                                        label={getDdayBadge(event?.date)}
                                                        variant="outlined"
                                                        sx={{ ...st.chipSx, fontWeight: 700 }}
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>

                                        <Divider sx={{ my: 1.25 }} />

                                        <Stack spacing={0.75}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <EventIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                                    {event.date || "-"}
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PlaceIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
                                                    {event.place || "-"}
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                                <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
                                                    {event.client || "-"}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>

                                    <Box sx={{ flexGrow: 1 }} />

                                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                                        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                            <ActionButton variant="primary" startIcon={<GroupIcon />}
                                                onClick={() => onGoParticipants?.(event.eventName)}
                                                sx={{ flex: 1 }} label="명단" />
                                            <ActionButton variant="edit"
                                                onClick={() => openModal("edit", event)}
                                                sx={{ flex: 1 }} />
                                            <ActionButton variant="delete"
                                                onClick={() => openModal("delete", event)}
                                                sx={{ flex: 1 }} />
                                            {/*<ActionButton variant="secondary" startIcon={<LinkIcon />}*/}
                                            {/*    onClick={() => openLinks(event)}*/}
                                            {/*    sx={{ flex: 1 }} label="링크" />*/}
                                        </Stack>
                                    </CardActions>
                                </Card>
                            );
                        })}
                    </Box>
                </Collapse>
            </Box>

            {/* empty */}
            {computed.total === 0 && (
                <Box
                    sx={{
                        mt: 2,
                        border: `1px dashed ${ds.colors.border}`,
                        borderRadius: 1.5,
                        py: 6,
                        textAlign: "center",
                        color: "text.secondary",
                    }}
                >
                    조건에 맞는 행사가 없습니다.
                </Box>
            )}

            {/* add/edit/delete dialog */}
            <FormModal
                open={openDialog}
                onClose={closeModal}
                title={mode === "add" ? "행사 추가" : mode === "edit" ? "행사 수정" : "행사 삭제"}
                maxWidth="sm"
                onSave={mode === "delete" ? handleDelete : handleSave}
                saveLabel={mode === "delete" ? "삭제" : "저장"}
                saveColor={mode === "delete" ? "danger" : "primary"}
            >
                {mode === "delete" ? (
                    <Typography>
                        정말로 <b>{currentEvent.eventName}</b> 행사를 삭제하시겠습니까?
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        <FormField label="행사명" required>
                            <TextField
                                name="eventName"
                                value={currentEvent.eventName || ""}
                                onChange={handleInputChange}
                                disabled={mode === "edit"}
                                placeholder="행사명을 입력하세요"
                                size="small"
                                fullWidth
                            />
                        </FormField>
                        <FormField label="행사 일시" required>
                            <TextField
                                type="date"
                                name="date"
                                value={currentEvent.date || ""}
                                onChange={handleInputChange}
                                size="small"
                                fullWidth
                                helperText="YYYY-MM-DD 또는 서버에서 YYYY-MM-DD HH:mm:ss 로 내려와도 정상 처리됩니다."
                            />
                        </FormField>
                        <FormField label="행사 위치" required>
                            <TextField name="place" size="small" value={currentEvent.place || ""} onChange={handleInputChange} placeholder="행사 위치를 입력하세요" fullWidth />
                        </FormField>
                        <FormField label="발주처" required>
                            <TextField name="client" size="small" value={currentEvent.client || ""} onChange={handleInputChange} placeholder="발주처를 입력하세요" fullWidth />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* 링크 생성 dialog */}
            <FormModal
                open={openLinkDialog}
                onClose={() => setOpenLinkDialog(false)}
                title="링크 생성"
                maxWidth="sm"
                cancelLabel="닫기"
            >
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                            참가 신청 폼 링크
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField value={linkRequest} size="small" fullWidth InputProps={{ readOnly: true }} />
                            <IconButton onClick={() => copy(linkRequest)} aria-label="copy">
                                <ContentCopyIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                            참가 내역 확인 페이지 링크
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField value={linkCheck} size="small" fullWidth InputProps={{ readOnly: true }} />
                            <IconButton onClick={() => copy(linkCheck)} aria-label="copy">
                                <ContentCopyIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        * eventName에 한글/공백이 있어도 안전하게 동작하도록 URL 인코딩 처리했습니다.
                    </Typography>
                </Stack>
            </FormModal>

        </Box>
    );
}
